import styled from "styled-components";

/**
 * The project's type scale.
 *
 * ## Why it is in px
 *
 * Measured: the document root is 16px and nothing in the app scales it. The rem values this file
 * used before resolved to 14.65px and 19.5px — they had been converted from a root of about 13px
 * that never existed here, which is where the odd sizes on screen came from. rem buys a scale
 * nothing to do with a root nobody moves, and costs numbers no one can reason about.
 *
 * ## Why these steps
 *
 * Counted across `src`: 14px appears 194 times (as `14px` and as `0.875rem`), 12px 149 times,
 * 16px 145, 18px 46, 20px 23, 24px 16, 11px 15. Every step below is a value the app already
 * converged on. Nothing here is invented, and 77 distinct sizes collapse to seven.
 *
 * ## Adjacent steps — read before adding a size
 *
 * `caption` and `label` are one pixel apart, and that is the most the scale should ever have.
 * A pair that close only works while each has a *role*, which is what the names are for: `caption`
 * is a chip or a footnote, `label` is the name of a field or a column. Choosing between them by eye
 * rather than by role is how the app got to 77 distinct sizes in the first place.
 *
 * There was a third, `dense` at 13px, added for a table row's attributes. It is gone: the accounts
 * table settled on 14 for those, nothing else asked for 13, and a step nobody uses is a step the
 * next reader has to choose against. If a genuinely denser table needs it, add it back then — with
 * that screen as the evidence.
 *
 * ## Why not CSS custom properties — settled, do not reopen
 *
 * The obvious alternative is `:root { --pl-font-size-body: 14px }`, which the 211 SCSS files could
 * read as well as the 81 styled-components ones. The app already has such a layer in
 * `design_2.0/_variables.scss`. It was considered and rejected on two measured grounds:
 *
 * 1. That layer is fed by the palette the kit replaced — `--color-primary: #{$primary-color}` where
 *    `$primary-color` is #6490bf, against the kit's `primary[900]` of #0085BE. It is the previous
 *    design system's bus, not a neutral one.
 * 2. Tests load no SCSS: `setupTests.js` imports none, and the global sheet is imported from
 *    `index.js`, which tests never run. So in jsdom the variables do not exist, and
 *    `toHaveStyleRule("font-size", "var(--pl-font-size-body)")` passes whatever the value is — or
 *    if there is none. Every size assertion in the kit would go value-blind. A missing custom
 *    property also does not fall back: the declaration is invalid at computed-value time and the
 *    size is inherited instead, so a typo is silent everywhere.
 *
 * If SCSS ever needs these values, feed `:root` *from* this file rather than moving them into it —
 * the numbers stay here, where a test can read them.
 *
 * ## Size and line-height only
 *
 * Weight is a separate axis and belongs to the call site: the same 14px is regular in a cell and
 * semibold when that cell carries the row's identity. Baking weight into the step would have
 * needed two tokens for every step, which is exactly the mistake in the deprecated block below —
 * `Heading5` and `Body1` are the same size and differ only in weight.
 */
export const type = {
    /** 11/16 — a chip, a footnote. The smallest text the app should set. */
    caption: { size: 11, height: 16 },
    /** 12/16 — the name of a field or a column, not its value. */
    label: { size: 12, height: 16 },
    /** 14/20 — prose, and the thing a row is about. The default. */
    body: { size: 14, height: 20 },
    /** 16/24 — a lead paragraph, or a value worth reading before the rest. */
    bodyLarge: { size: 16, height: 24 },
    /** 18/27 — a card or panel title. Measured across 11 card titles before this existed. */
    sectionTitle: { size: 18, height: 27 },
    /** 20/28 — a page section above several cards. */
    title: { size: 20, height: 28 },
    /** 24/32 — the page. */
    pageTitle: { size: 24, height: 32 },
};

/**
 * The step as CSS, for a styled block that cannot wrap its content in a component — a table cell,
 * a pseudo-element, anything where the type has to be declared rather than rendered.
 *
 *     const Cell = styled.td`
 *         ${typeStyle("body")}
 *         font-weight: 400;
 *     `;
 */
export const typeStyle = (name) => `
    font-size: ${type[name].size}px;
    line-height: ${type[name].height}px;
`;

/* ---------- components ---------- */

/** 14/20/400 — the default. */
export const Body = styled.span`
    ${typeStyle("body")}
    font-weight: 400;
`;

/** 14/20/600 — the same step, carrying the emphasis. */
export const BodyStrong = styled.span`
    ${typeStyle("body")}
    font-weight: 600;
`;

/** 12/16/500 — the name of a field or column. */
export const FieldLabel = styled.span`
    ${typeStyle("label")}
    font-weight: 500;
`;

/** 11/16/500 — a chip, a footnote. */
export const Caption = styled.span`
    ${typeStyle("caption")}
    font-weight: 500;
`;

/** 18/27/600 — a card or panel title. */
export const SectionTitle = styled.span`
    ${typeStyle("sectionTitle")}
    font-weight: 600;
`;

/** 20/28/600 — a page section above several cards. */
export const Title = styled.span`
    ${typeStyle("title")}
    font-weight: 600;
`;

/** 24/32/600 — the page. */
export const PageTitle = styled.span`
    ${typeStyle("pageTitle")}
    font-weight: 600;
`;

/* ---------- deprecated ---------- */

/**
 * Frozen exactly as they were: same declarations, same rem, byte for byte.
 *
 * Every one of these is a rem value converted from a root this app does not have, which is why
 * `Heading3` is 19.5px and `Heading5` is 14.65px. They are not re-pointed at the scale above,
 * because 21 files import them and re-pointing would change all of those screens at once without
 * anyone looking. Each is replaced when the screen that uses it is next touched.
 *
 * The mapping to use when you do:
 *
 *   Heading1 29px/500  → PageTitle, or `type.pageTitle` if 24 is enough
 *   Heading2 24px/500  → PageTitle
 *   Heading3 19.5px/600 → Title
 *   Heading4 17px/600  → SectionTitle
 *   Heading5 14.65px/500 → BodyStrong at 500, or SectionTitle if it is a title
 *   Heading6 12px/500  → FieldLabel
 *   Body1    14.65px/400 → Body
 *   Body2    12px/400  → FieldLabel at 400
 *
 * `Heading5` and `Body1` are the same size and differ only in weight, as are `Heading6` and
 * `Body2` — the names say "heading" about something that is not one.
 *
 * @deprecated Use the scale above.
 */
export const Heading1 = styled.span`
    font-size: 1.8125rem;
    font-weight: 500;
    line-height: 2.2625rem;
    letter-spacing: -0.58px;
`;

/** @deprecated Use PageTitle. */
export const Heading2 = styled.span`
    font-size: 1.5rem;
    font-weight: 500;
    line-height: 1.875rem;
    letter-spacing: -0.384px;
`;

/** @deprecated Use Title. */
export const Heading3 = styled.span`
    font-size: 1.21875rem;
    font-weight: 600;
    line-height: 1.7688rem;
    letter-spacing: -0.234px;
`;

/** @deprecated Use SectionTitle. */
export const Heading4 = styled.span`
    font-size: 1.0625rem;
    font-weight: 600;
    line-height: 1.5063rem;
    letter-spacing: -0.136px;
`;

/** @deprecated Use BodyStrong, or SectionTitle if it is a title. */
export const Heading5 = styled.span`
    font-size: 0.91563rem;
    font-weight: 500;
    line-height: 1.2938rem;
`;

/** @deprecated Use FieldLabel. */
export const Heading6 = styled.span`
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.0638rem;
`;

/** @deprecated Use Body. */
export const Body1 = styled.span`
    font-size: 0.91563rem;
    font-weight: 400;
    line-height: 1.2938rem;
`;

/** @deprecated Use FieldLabel at weight 400. */
export const Body2 = styled.span`
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1.0638rem;
`;

/**
 * Extension-only, and left alone: both carry a letter-spacing the scale above does not, and the
 * extension is the other product. Fold them in when it moves to the scale.
 */
export const ExtensionBody = styled.span`
    font-size: 14px;
    font-weight: 500;
    letter-spacing: -0.17px;
    line-height: 21px;
`;

export const ExtensionSmall = styled.span`
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0px;
`;
